import { Core } from "@faire/design-tokens";
import { StrictLocalizeFunction } from "@faire/web/common/localization";
import { Span } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";

import { FundingSource } from "@faire/retailer/components/OpenWithFaire/OpenWithFaireApplication/FundingSource";
import { INestedCheckboxOption } from "@faire/retailer/components/OpenWithFaire/OpenWithFaireApplication/NestedCheckboxes/NestedCheckboxes";

const ColorFormatting = (chunk: JSX.Element): JSX.Element => (
  <Span color={Core.text.subdued}>{chunk}</Span>
);

export const fundingOptions = (
  isEurope: boolean | undefined,
  strictLocalize: StrictLocalizeFunction
): INestedCheckboxOption[] => [
  {
    key: FundingSource.PERSONAL_CASH,
    label: strictLocalize({
      defaultMessage: "Personal cash",
      description: {
        text: "A possible answer for `Where have you received the cash/financing from?`. User can select multiple options.",
        img: "https://cdn.faire.com/fastly/b0a296b5aaf10421dab653b8ee518bc9bb606206654a275c9a685a36d05d657e.png",
      },
    }),
    children: [
      {
        key: FundingSource.PERSONAL_SAVINGS,
        label: strictLocalize({
          defaultMessage: "Personal savings",
          description: {
            text: "A possible answer for `Where have you received the cash/financing from?`. User can select multiple options.",
            img: "https://cdn.faire.com/fastly/b0a296b5aaf10421dab653b8ee518bc9bb606206654a275c9a685a36d05d657e.png",
          },
        }),
      },
      {
        key: FundingSource.INCOME,
        label: strictLocalize({
          defaultMessage: "Income",
          description: {
            text: "A possible answer for `Where have you received the cash/financing from?`. User can select multiple options.",
            img: "https://cdn.faire.com/fastly/b0a296b5aaf10421dab653b8ee518bc9bb606206654a275c9a685a36d05d657e.png",
          },
        }),
      },
      {
        key: FundingSource.GIFT,
        label: strictLocalize({
          defaultMessage: "Gift",
          description: {
            text: "A possible answer for `Where have you received the cash/financing from?`. User can select multiple options.",
            img: "https://cdn.faire.com/fastly/b0a296b5aaf10421dab653b8ee518bc9bb606206654a275c9a685a36d05d657e.png",
          },
        }),
      },
      {
        key: FundingSource.CASH_FROM_CROWDFUNDING,
        label: strictLocalize({
          defaultMessage: "Cash from crowdfunding",
          description: {
            text: "A possible answer for `Where have you received the cash/financing from?`. User can select multiple options.",
            img: "https://cdn.faire.com/fastly/b0a296b5aaf10421dab653b8ee518bc9bb606206654a275c9a685a36d05d657e.png",
          },
        }),
      },
    ],
  },
  {
    key: FundingSource.LOANS,
    label: strictLocalize({
      defaultMessage: "Loans",
      description: {
        text: "A possible answer for `Where have you received the cash/financing from?`. User can select multiple options.",
        img: "https://cdn.faire.com/fastly/47ff1df5ee86d19ddf99d249bfb9844007b2e94b39e35cab6ba2ed058dc30ca5.png",
      },
    }),
    children: [
      {
        key: FundingSource.SBA_LOAN,
        label: strictLocalize({
          defaultMessage: "SBA loan",
          description: {
            text: "A possible answer for `Where have you received the cash/financing from?`. User can select multiple options.",
            img: "https://cdn.faire.com/fastly/47ff1df5ee86d19ddf99d249bfb9844007b2e94b39e35cab6ba2ed058dc30ca5.png",
          },
        }),
      },
      {
        key: FundingSource.BANK_LOAN,
        label: strictLocalize({
          defaultMessage: "Business loan from bank",
          description: {
            text: "A possible answer for `Where have you received the cash/financing from?`. User can select multiple options.",
            img: "https://cdn.faire.com/fastly/47ff1df5ee86d19ddf99d249bfb9844007b2e94b39e35cab6ba2ed058dc30ca5.png",
          },
        }),
      },
      {
        key: FundingSource.PERSONAL_CONSUMER_LOAN,
        label: strictLocalize({
          defaultMessage: "Personal/consumer loan",
          description: {
            text: "A possible answer for `Where have you received the cash/financing from?`. User can select multiple options.",
            img: "https://cdn.faire.com/fastly/47ff1df5ee86d19ddf99d249bfb9844007b2e94b39e35cab6ba2ed058dc30ca5.png",
          },
        }),
      },
      {
        key: FundingSource.ONLINE_LENDER,
        formattedText: isEurope ? (
          <StrictLocalMsg
            defaultMessage="Business loan from an online lender <ColorFormatting>(Clearpay, Clearly, Tide, YouLend, Funding Circle, etc)</ColorFormatting>"
            description={{
              text: "A possible answer for `Where have you received the cash/financing from?`. User can select multiple options.",
              img: "https://cdn.faire.com/fastly/92f5597cfb54587b68059fbd1bc392dc8935d3a3c64f9726d8e3f0b8891d5e93.png",
            }}
            values={{ ColorFormatting }}
          />
        ) : (
          <StrictLocalMsg
            defaultMessage="Business loan from an online lender <ColorFormatting>(Kabbage, Bluevine, OnDeck, Fundbox, etc)</ColorFormatting>"
            values={{ ColorFormatting }}
          />
        ),
      },
    ],
  },
  {
    key: FundingSource.OUTSIDE_INVESTOR,
    label: strictLocalize({
      defaultMessage: "Outside investor",
      description: {
        text: "A possible answer for `Where have you received the cash/financing from?`. User can select multiple options.",
        img: "https://cdn.faire.com/fastly/b0a296b5aaf10421dab653b8ee518bc9bb606206654a275c9a685a36d05d657e.png",
      },
    }),
  },
  {
    key: FundingSource.GRANT,
    label: strictLocalize({
      defaultMessage: "Grant",
      description: {
        text: "A possible answer for `Where have you received the cash/financing from?`. User can select multiple options.",
        img: "https://cdn.faire.com/fastly/b0a296b5aaf10421dab653b8ee518bc9bb606206654a275c9a685a36d05d657e.png",
      },
    }),
  },
];
